import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import GoogleLogin from "./google-login";
import { AuthorizationRequestFields } from "src/lib/itaptec/http_client/src";
import { CodeResponse } from "@react-oauth/google";
import React, { useState } from "react";
import { Key, Visibility, VisibilityOff, Email } from '@mui/icons-material'

const schema = z.object({
  [AuthorizationRequestFields.Mail]: z.string(),
  [AuthorizationRequestFields.Password]: z.string(),
  [AuthorizationRequestFields.ClientIp]: z.string(),
});

type SchemaType = z.infer<typeof schema>;

// TODO: la idea era que el botón de Google y esos sean opcionales, pero un bug con
// versiones de @mui/icons-material me forzó a sacarlo hasta saber cómo resolverlo.

// const getSchemaWithValidationFunction = (validateLogin: LoginCallback) => {
//   return schema.superRefine(async (data, ctx) => {
//     const issues = await validateLogin(data);

//     issues.forEach((issue) => {
//       ctx.addIssue({
//         path: [issue.path],
//         message: issue.message,
//         code: z.ZodIssueCode.custom,
//       });
//     });
//   });
// };

// type LoginCallback = (
//   data: SchemaType
// ) => Promise<ValidationError<SchemaType>[]>;

export type LoginProps = {
  // validateLogin?: LoginCallback;
  /**
   * Objeto para indicar qué métodos de autenticación exponer.
   */
  loginOptions: {
    internal?: boolean,
    google?: boolean
  },
  /**
   * Submit handler to execute when RHF successfully validates the form.
   */
  onValid?: SubmitHandler<SchemaType>;
  /**
   * Submit error handler when RHF catches a validation error.
   */
  onInvalid?: SubmitErrorHandler<SchemaType>;
  /**
   * Callback to execute when successful login with Google.
   */
  onGoogleLogin?: (
    response: Omit<CodeResponse, "error" | "error_description" | "error_uri">
  ) => void;
  /**
   * Override the texts on the component.
   */
  labels?: {
    welcome?: string;
    pleaseLogIn?: string;
    or?: string;
    logIn?: string;
    loginWithGoogle?: string;
  };
  /**
   * Path to the image logo. If not provided, no logo will be shown.
   */
  logo?: string;
  /**
   * Override the components.
   */
  components?: {
    googleLogin?: React.ComponentType<{ onClick?: () => void }>;
  };
  /**
   * Add and override components props.
   */
  overrideProps?: {
    buttonProps?: Omit<React.ComponentProps<typeof Button>, "onClick">;
    dividerProps?: React.ComponentProps<typeof Divider>;
    imgProps?: Omit<React.ComponentProps<"img">, "src">;
  };
};

const Login = (props: LoginProps) => {
  const {
    // validateLogin,
    loginOptions,
    onValid,
    onInvalid,
    onGoogleLogin,
    logo,
    labels,
    components,
    overrideProps,
  } = props;

  const { control, handleSubmit, formState } = useForm<SchemaType>({
    // resolver: zodResolver(
    //   validateLogin ? getSchemaWithValidationFunction(validateLogin) : schema
    // ),
    resolver: zodResolver(schema),
    reValidateMode: "onSubmit", // Revalidar sólo onSubmit. Sino va a revalidar con el server en cada cambio.
    defaultValues: {
      [AuthorizationRequestFields.Mail]: "",
      [AuthorizationRequestFields.Password]: "",
      [AuthorizationRequestFields.ClientIp]: "",
    },
  });

  const [showPassword, setShowPassword] = useState<boolean>(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  return (
    <Box>
      <Stack spacing={4}>
        <Box>
          <Stack direction="row" spacing={2} sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {labels?.welcome || "Bienvenido! 👋🏻"}
            </Typography>
            {logo && (
              <img
                height={24}
                alt="logo"
                src={logo}
                {...overrideProps?.imgProps}
              />
            )}
          </Stack>
          <Box>
            <Typography variant="body2">
              {labels?.pleaseLogIn ||
                "Por favor, inicie sesión para ingresar al sistema."}
            </Typography>
          </Box>
        </Box>
        {
          loginOptions.internal &&
          <>
            <Box
              sx={{
                my: 2,
              }}
            >
              <form onSubmit={onValid && handleSubmit(onValid, onInvalid)}>
                <Stack spacing={2}>
                  <Controller
                    control={control}
                    name={AuthorizationRequestFields.Mail}
                    render={({ field, fieldState }) => {
                      return (
                        <TextField
                          {...field}
                          type="email"
                          id="email"
                          fullWidth
                          size="small"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Email />
                              </InputAdornment>
                            )
                          }}
                        />
                      );
                    }}
                  />
                  <Controller
                    control={control}
                    name={AuthorizationRequestFields.Password}
                    render={({ field, fieldState }) => {
                      return (
                        <TextField
                          {...field}
                          type={showPassword ? "text" : "password"}
                          fullWidth
                          size="small"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Key />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={handleClickShowPassword}
                                  edge="end"
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />
                      );
                    }}
                  />
                  {/* {formState.errors &&
              Object.entries(formState.errors).map(([field, error]) => {
                return <InputLabel error>{error.message}</InputLabel>;
              })} */}
                  <Button
                    variant="contained"
                    type="submit"
                    {...overrideProps?.buttonProps}
                  >
                    {labels?.logIn || "Ingresar"}
                  </Button>
                </Stack>
              </form>
            </Box>
            {loginOptions.google && (
              <Divider {...overrideProps?.dividerProps}>
                {labels?.or || "ó"}
              </Divider>
            )}
          </>
        }
        <Stack spacing={2}>
          {loginOptions.google && (
            <GoogleLogin
              component={components?.googleLogin}
              onSuccess={onGoogleLogin || (() => {console.log()})}
              buttonLabel={labels?.loginWithGoogle}
            />
          )}
        </Stack>
      </Stack >
    </Box >
  );
};

export default Login;
